import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

export default function Login({ goToSignup, goToForgotPassword, goToPrivacy, statusUrl }) {
  const resolvedStatusUrl = statusUrl || process.env.REACT_APP_STATUS_URL;
  const { login, verifyMfa } = useAuth();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaToken, setMfaToken] = useState(null); // set when 2FA is required
  const [code, setCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await login(form.username, form.password);
      if (res && res.mfa_required) { setMfaToken(res.mfa_token); }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    }
    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyMfa(mfaToken, code.trim());
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed');
    }
    setLoading(false);
  };

  const resetToPassword = () => { setMfaToken(null); setCode(''); setError(''); setForm(f => ({ ...f, password: '' })); };

  // ── Second step: 2FA code entry ────────────────────────────────────────────
  if (mfaToken) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', padding: 16 }}>
        <div style={{ background: 'var(--glass-2)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', border: '1px solid var(--glass-border)', borderRadius: 22, padding: '40px 36px', width: 380, maxWidth: '100%', boxShadow: '0 24px 60px rgba(15,23,42,.22)', color: 'var(--text)' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text)' }}>Two-factor authentication</div>
            <div style={{ color: 'var(--muted)', marginTop: 6, fontSize: '0.9rem' }}>Enter the 6-digit code from your authenticator app, or a backup code.</div>
          </div>
          <form onSubmit={handleVerify}>
            <div className="form-group">
              <label>Authentication code</label>
              <input
                autoFocus
                value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="123456"
                inputMode="text"
                autoComplete="one-time-code"
                style={{ letterSpacing: '0.2em', fontSize: '1.1rem', textAlign: 'center' }}
                required
              />
            </div>
            {error && (
              <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 8, marginBottom: 14, fontSize: '0.875rem' }}>{error}</div>
            )}
            <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', justifyContent: 'center', padding: '10px', marginTop: 4 }}>
              {loading ? 'Verifying…' : 'Verify'}
            </button>
          </form>
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button onClick={resetToPassword} style={{ background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: '0.875rem' }}>← Back to sign in</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', padding: 16 }}>
      <div style={{ background: 'var(--glass-2)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', border: '1px solid var(--glass-border)', borderRadius: 22, padding: '40px 36px', width: 380, maxWidth: '100%', boxShadow: '0 24px 60px rgba(15,23,42,.22)', color: 'var(--text)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text)' }}>HR<span style={{ color: 'var(--primary-strong)' }}>Track</span></div>
          <div style={{ color: 'var(--muted)', marginTop: 6, fontSize: '0.9rem' }}>Sign in to your account</div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Username or Email</label>
            <input
              autoFocus
              value={form.username}
              onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
              placeholder="username or you@email.com"
              required
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder="••••••••"
              required
            />
          </div>

          {error && (
            <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 8, marginBottom: 14, fontSize: '0.875rem' }}>
              {error}
            </div>
          )}

          <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', justifyContent: 'center', padding: '10px', marginTop: 4 }}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button onClick={goToForgotPassword} style={{ background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: '0.875rem' }}>
            Forgot password?
          </button>
        </div>

        {goToSignup && (
          <p style={{ textAlign: 'center', marginTop: 20, fontSize: '0.875rem', color: 'var(--muted)', borderTop: '1px solid var(--hairline)', paddingTop: 20 }}>
            New to HRTrack?{' '}
            <button onClick={goToSignup} style={{ background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontWeight: 600 }}>
              Create a workspace →
            </button>
          </p>
        )}
        <p style={{ textAlign: 'center', marginTop: 12, fontSize: '0.8rem', display: 'flex', gap: 14, justifyContent: 'center' }}>
          {goToPrivacy && (
            <button onClick={goToPrivacy} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', textDecoration: 'underline' }}>
              Privacy Policy
            </button>
          )}
          {resolvedStatusUrl && (
            <a href={resolvedStatusUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--muted)', textDecoration: 'underline' }}>
              System status ↗
            </a>
          )}
        </p>
      </div>
    </div>
  );
}
